import { z } from 'zod';
import { LENDER_KINDS, LOAN_KINDS, LOAN_STATUSES, MORATORIUM_MODES, REPAYMENT_FREQUENCIES, REPAYMENT_MODES, REPAYMENT_STRUCTURES, } from '../enums.js';
import { bankAccountNumber, basisPoints, ifsc, isoDate, minorAmount, objectId, paginationQuery, pan, scopeQuery, } from './common.js';
const positiveAmount = minorAmount.refine((amount) => amount > 0, 'Enter an amount above 0');
const optionalId = objectId.optional().or(z.literal(''));
/** Who lent it, kept on the loan. Bank-transfer loans need the lender's account. */
export const loanLender = z.object({
    name: z.string().trim().min(2, 'Enter the lender’s name').max(120),
    kind: z.enum(LENDER_KINDS),
    pan: pan.optional().or(z.literal('')),
    beneficiaryName: z.string().trim().max(120).optional(),
    accountNumber: bankAccountNumber.optional().or(z.literal('')),
    ifsc: ifsc.optional().or(z.literal('')),
});
/** The terms the schedule is worked out from. */
export const loanTerms = z.object({
    disbursedAmount: positiveAmount,
    rateBasisPoints: basisPoints,
    frequency: z.enum(REPAYMENT_FREQUENCIES),
    structure: z.enum(REPAYMENT_STRUCTURES),
    instalmentCount: z.coerce.number().int().min(1, 'At least one instalment').max(600),
    firstDueDate: isoDate,
    moratorium: z
        .object({
        periods: z.coerce.number().int().min(1).max(60),
        mode: z.enum(MORATORIUM_MODES),
    })
        .optional()
        .nullable(),
});
const loanFields = loanTerms.extend({
    lender: loanLender,
    loanAccountNumber: z.string().trim().min(3, 'Enter the loan account number').max(40),
    kind: z.enum(LOAN_KINDS),
    bankAccountId: objectId,
    sanctionedAmount: positiveAmount,
    disbursedOn: isoDate.optional(),
    repaymentMode: z.enum(REPAYMENT_MODES),
    processingFee: z
        .object({ amount: positiveAmount, mode: z.enum(['DEDUCTED', 'PAID_SEPARATELY']) })
        .optional()
        .nullable(),
    penalRateBasisPoints: basisPoints.optional(),
    tds: z
        .object({
        applicable: z.boolean(),
        rateBasisPoints: basisPoints.refine((rate) => rate > 0, 'Enter a rate above 0%'),
    })
        .optional()
        .nullable(),
    principalLedgerId: optionalId,
    interestLedgerId: optionalId,
    notes: z.string().trim().max(1000).optional(),
});
/** Paid by transfer, the lender's account is where the money goes. */
const withTransferAccount = (schema) => schema.superRefine((loan, ctx) => {
    if (loan.repaymentMode === 'BANK_TRANSFER' && !loan.lender?.accountNumber) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lender', 'accountNumber'],
            message: 'A loan repaid by transfer needs the lender’s account number',
        });
    }
    if (loan.disbursedAmount > loan.sanctionedAmount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['disbursedAmount'],
            message: 'Disbursed cannot be more than sanctioned',
        });
    }
});
export const createLoanRequest = withTransferAccount(loanFields.extend({ companyId: objectId }));
/** A draft's terms can still change; an active loan's only its notes and ledgers. */
export const updateLoanRequest = loanFields.partial();
/** Works out a schedule without saving anything. */
export const loanSchedulePreviewRequest = loanTerms;
/**
 * A change to one instalment of an auto-debit loan, where the bank's debit
 * differed from the schedule. Before it is matched to a bank debit its date
 * and amount can change; after, only the split, TDS and note.
 */
export const editInstalmentRequest = z
    .object({
    dueDate: isoDate.optional(),
    principal: minorAmount.optional(),
    interest: minorAmount.optional(),
    tdsAmount: minorAmount.optional(),
    bankReference: z.string().trim().max(60).optional(),
    note: z.string().trim().max(500).optional(),
    reason: z.string().trim().min(3, 'Say why it is being changed').max(500),
})
    .refine((edit) => ['dueDate', 'principal', 'interest', 'tdsAmount', 'bankReference', 'note'].some((key) => edit[key] !== undefined), 'Nothing to change');
/** A penal charge on a late instalment, entered by hand. */
export const addLoanChargeRequest = z.object({
    instalmentId: objectId.optional(),
    amount: positiveAmount,
    reason: z.string().trim().min(3, 'Say what the charge is for').max(500),
    dueDate: isoDate.optional(),
});
export const LOAN_SORTS = ['outstanding', 'nextDue', 'emi', 'rate', 'lender', 'reference'];
export const loanListQuery = paginationQuery.merge(scopeQuery).extend({
    q: z.string().trim().max(80).optional(),
    status: z.enum(LOAN_STATUSES).optional(),
    kind: z.enum(LOAN_KINDS).optional(),
    lenderKind: z.enum(LENDER_KINDS).optional(),
    bankAccountId: objectId.optional(),
    repaymentMode: z.enum(REPAYMENT_MODES).optional(),
    dueWithinDays: z.coerce.number().int().min(0).max(366).optional(),
    overdue: z.enum(['true', 'false']).optional(),
    sort: z.enum(LOAN_SORTS).optional(),
    direction: z.enum(['asc', 'desc']).optional(),
});
//# sourceMappingURL=loans.js.map